import { useEffect, useMemo, useRef, useState } from 'react'
import { T } from '@/lib/theme'
import { Button, Panel, SectionLabel, ToggleButton } from '@/components/ui'
import { ADDRESSES } from '@/lib/contracts'
import protectionAbi from '@/lib/abis/ProtectionManager.json'
import { useAction } from '@/hooks/useAction'
import {
  useProtection, hfToWei,
  DEFAULT_TRIGGER_HF, DEFAULT_TARGET_HF,
} from '@/hooks/useProtection'

const MAX_UINT256 = (1n << 256n) - 1n

/// The three trigger points offered, and why the tightest one is not the default.
///
/// The repay a protection needs grows as health falls, so a trigger of 1.05 authorises a LARGER
/// collateral sale under WORSE conditions than one of 1.15. Tighter is not safer here; it is later
/// and more expensive. Each preset carries its own target, always clearing its trigger by more than
/// the contract's 0.02 minimum so a protection cannot leave the account still inside its own band.
const PRESETS = [
  { key: 'early', label: 'Early', trigger: 1.30, target: 1.60, blurb: 'Acts well before trouble. Smallest sale, most often.' },
  { key: 'balanced', label: 'Balanced', trigger: DEFAULT_TRIGGER_HF, target: DEFAULT_TARGET_HF, blurb: 'Recommended. Acts with room to spare.' },
  { key: 'late', label: 'Late', trigger: 1.05, target: 1.25, blurb: 'Acts close to liquidation. Largest sale, fewest times.' },
]

/// Opt-in liquidation protection, one toggle row at rest.
///
/// **Progressive disclosure, deliberately.** The first version rendered every setting and every
/// disclosure inline and ate ~400px explaining itself; the founder's verdict was "does it need all
/// this writing". The rework keeps all of it but moves each piece to the moment it matters:
/// - At rest: one row. Label, honest status chip, the live setting once one exists, a switch.
/// - Turning the switch on opens the CONFIRM step rather than firing a transaction: preset choice,
///   the pair, and the two disclosures someone should actually read before approving (the trigger
///   is public; the sToken approval is the real authorisation). Tooltips were rejected for those
///   two because tooltips are read by nobody, and the confirm step is the one moment the user is
///   reading. Turning the switch off fires disable directly; that direction needs no ceremony.
/// - The three venue/keeper warning paragraphs collapsed into the status chip plus a single
///   "goes live once ..." line naming the first unmet condition.
///
/// **The status chip still shows five states rather than on/off.** Enabling is not the same as
/// being protected: the approval, the venue and the venue's maturity all have to be true as well,
/// and a switch that reads "on" while any of them is false tells a user their position is defended
/// when it is not. `useProtection` returns the states apart for exactly this reason; the chip is
/// where they stay visible now that the prose is gone.
export function ProtectionPanel({ d, onDone }) {
  const action = useAction()
  const [expanded, setExpanded] = useState(false)

  // Largest position on each side, which is what a protection would actually act on. The user can
  // change either; these are only the starting point.
  const collateralOptions = useMemo(
    () => d.reserves.filter((r) => (r.user?.supplyBalance ?? 0) > 0 && r.sTokenAddress),
    [d.reserves],
  )
  const debtOptions = useMemo(() => d.reserves.filter((r) => (r.user?.debt ?? 0) > 0), [d.reserves])

  const [collateralKey, setCollateralKey] = useState(null)
  const [debtKey, setDebtKey] = useState(null)
  const [presetKey, setPresetKey] = useState('balanced')

  const biggest = (list, field) => [...list].sort((a, b) => (b.user?.[field] ?? 0) - (a.user?.[field] ?? 0))[0]
  const collateral = collateralOptions.find((r) => r.key === collateralKey) ?? biggest(collateralOptions, 'supplyBalance')
  const debt = debtOptions.find((r) => r.key === debtKey) ?? biggest(debtOptions, 'debt')

  const p = useProtection({
    collateralAsset: collateral?.asset,
    debtAsset: debt?.asset,
    sTokenAddress: collateral?.sTokenAddress,
  })

  if (!p.deployed || !d.hasDebt || !collateral || !debt) return null

  const preset = PRESETS.find((x) => x.key === presetKey) ?? PRESETS[1]
  // The on-chain trigger mapped back to a preset for the resting row; anything else reads Custom.
  const livePreset = p.enabled
    ? PRESETS.find((x) => Math.abs(x.trigger - (p.triggerHf ?? 0)) < 0.005)
    : null
  const after = () => { p.refetch?.(); onDone?.() }

  async function enable() {
    // The collateral-sToken approval to the ProtectionManager (the "authorisation that matters"
    // called out in the disclosure below) is folded into the same intent as enable, so turning
    // protection on is one confirmation on a batch wallet. The pull amount is MAX so it only
    // re-approves when the allowance is not already infinite; once approved, enabling/updating the
    // preset carries no approval leg at all.
    const res = await action.run({
      pulls: [{ token: collateral.sTokenAddress, spender: ADDRESSES.PROTECTION_MANAGER, amount: MAX_UINT256 }],
      calls: [{ address: ADDRESSES.PROTECTION_MANAGER, abi: protectionAbi, functionName: 'enable', args: [collateral.asset, debt.asset, hfToWei(preset.trigger), hfToWei(preset.target), 100] }],
      successMsg: 'Liquidation protection enabled',
    })
    if (!res) return
    setExpanded(false)
    after()
  }

  async function disable() {
    const res = await action.run({ calls: [{ address: ADDRESSES.PROTECTION_MANAGER, abi: protectionAbi, functionName: 'disable', args: [collateral.asset, debt.asset] }], successMsg: 'Liquidation protection turned off' })
    if (!res) return
    setExpanded(false)
    after()
  }

  function openEditor() {
    if (livePreset) setPresetKey(livePreset.key)
    setExpanded(true)
  }

  // The first unmet liveness condition, one line, same priority order as the chip.
  const pendingLine = !p.venueConfigured
    ? `Goes live once genesis opens the ${collateral.symbol} to ${debt.symbol} swap venue. Your setting waits for it.`
    : !p.venueReady
      ? `Goes live once the ${collateral.symbol} to ${debt.symbol} venue finishes building its price history.`
      : p.configured && p.anyKeeper !== true
        ? 'Goes live once a keeper joins this network.'
        : null

  return (
    <Panel style={{ padding: '20px 32px', marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <SectionLabel>Liquidation Protection</SectionLabel>
          <StatusChip p={p} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {p.enabled && !expanded && (
            <>
              <span style={summaryText}>
                {(livePreset?.label ?? 'Custom')} · acts at {p.triggerHf?.toFixed(2)}, recovers to {p.targetHf?.toFixed(2)}
              </span>
              <button onClick={openEditor} style={editLink}>Edit</button>
            </>
          )}
          <Switch
            on={p.enabled}
            disabled={action.pending}
            onClick={() => (p.enabled ? disable() : setExpanded(true))}
          />
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 18 }}>
          <p style={bodyDim}>
            Sells a slice of your collateral to repay debt at the level you pick, so the position
            recovers at market price ahead of any liquidator.
          </p>

          <div style={{ marginTop: 16 }}>
            <SectionLabel style={{ marginBottom: 8 }}>When to act</SectionLabel>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PRESETS.map((x) => (
                <ToggleButton key={x.key} active={x.key === presetKey} onClick={() => setPresetKey(x.key)}>
                  {x.label} · {x.trigger.toFixed(2)}
                </ToggleButton>
              ))}
            </div>
            <p style={{ ...bodyDim, marginTop: 8 }}>
              {preset.blurb} Acts at {preset.trigger.toFixed(2)}, recovers to {preset.target.toFixed(2)}.
              Liquidation sits at 1.00.
            </p>
          </div>

          <div style={{ marginTop: 16, display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', maxWidth: 480 }}>
            <PairSelect
              label="Sell this collateral"
              options={collateralOptions}
              value={collateral.key}
              onChange={setCollateralKey}
            />
            <PairSelect
              label="To repay this debt"
              options={debtOptions}
              value={debt.key}
              onChange={setDebtKey}
            />
          </div>

          {/* The two disclosures that matter for trust, placed at the moment of signing. They moved
              here from permanent inline prose and were deliberately kept out of tooltips: this is
              the one moment the user is actually reading. */}
          <div style={disclosureBox}>
            <p style={disclosureLine}>
              Your trigger is public once set, so the level where this position deleverages is
              visible to anyone watching. Every swap clears a time averaged price floor, which keeps
              that exposure small.
            </p>
            <p style={{ ...disclosureLine, marginTop: 8 }}>
              The {collateral.symbol} approval is the authorisation that matters. It lets protection
              repay your own debt and return the surplus to you, and only while your health factor
              sits below your trigger. Revoking it ends this contract's reach.
            </p>
          </div>

          {pendingLine && <p style={pendingText}>{pendingLine}</p>}

          <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {!p.enabled ? (
              <Button variant="primary" disabled={action.pending} onClick={enable}>
                Turn protection on
              </Button>
            ) : (
              <Button variant="primary" disabled={action.pending} onClick={enable}>
                Update to {preset.label}
              </Button>
            )}
            <button onClick={() => setExpanded(false)} style={editLink}>Cancel</button>
          </div>
        </div>
      )}
    </Panel>
  )
}

/// One chip, five possible readings - never a plain on/off, because four of the five are states
/// where the setting says on and the position is not actually defended. The order matters: it
/// reports the FIRST unmet condition, which is the one the user can act on next.
function StatusChip({ p }) {
  const [label, color] = !p.enabled
    ? ['Off', T.textDim]
    : !p.approved
      ? ['Approval pending', T.warning]
      : !p.venueReady
        ? ['Venue pending', T.warning]
        : p.anyKeeper === true
          ? ['Active', T.positive]
          : ['Keeper pending', T.warning]
  return (
    <span className="mono" style={{
      color, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
      letterSpacing: '0.06em', textTransform: 'uppercase',
    }}>{label}</span>
  )
}

function Switch({ on, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={on ? 'Turn protection off' : 'Turn protection on'}
      style={{
        width: 42, height: 24, borderRadius: 12, flexShrink: 0,
        border: `1px solid ${on ? T.positive : T.border}`,
        background: on ? T.positiveDim : 'rgba(255,255,255,0.06)',
        position: 'relative', padding: 0,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
        transition: 'background 0.2s ease, border-color 0.2s ease',
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: on ? 21 : 3, width: 16, height: 16,
        borderRadius: '50%', background: on ? T.positive : T.textDim,
        transition: 'left 0.2s ease, background 0.2s ease',
      }} />
    </button>
  )
}

/// Themed dropdown (a native <select>'s option popup is OS-drawn and ignores the dark theme - the
/// white menu with a grey highlight). A button trigger plus an absolutely-positioned themed menu,
/// matching VaultDetailSection's TokenSelect. Keyboard-operable (button trigger + button options)
/// and closes on outside click or Escape.
function PairSelect({ label, options, value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])
  const current = options.find((r) => r.key === value)
  return (
    <div style={{ display: 'block' }}>
      <SectionLabel style={{ marginBottom: 6 }}>{label}</SectionLabel>
      <div ref={ref} style={{ position: 'relative' }}>
        <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} style={pairSelectBtn}>
          <span style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 600 }}>{current?.symbol ?? '—'}</span>
          <span style={{ marginLeft: 'auto', color: T.textDim, fontSize: 11, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>▾</span>
        </button>
        {open && (
          <div role="listbox" style={pairSelectMenu}>
            {options.map((r) => {
              const active = r.key === value
              return (
                <button
                  key={r.key} type="button" role="option" aria-selected={active}
                  onClick={() => { onChange(r.key); setOpen(false) }}
                  style={{ ...pairSelectItem, background: active ? T.accentDim : 'transparent', color: active ? T.text : T.textDim }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent' }}
                >
                  <span style={{ fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 600 }}>{r.symbol}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const pairSelectBtn = {
  display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '10px 12px',
  borderRadius: T.r.md, border: `1px solid ${T.border}`, background: T.bg2,
  color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, cursor: 'pointer',
}
const pairSelectMenu = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 20,
  background: 'rgba(10,14,26,0.98)', backdropFilter: T.glassBlur, WebkitBackdropFilter: T.glassBlur,
  border: `1px solid ${T.border}`, borderRadius: T.r.md, boxShadow: T.shadowMed, overflow: 'hidden',
}
const pairSelectItem = {
  display: 'flex', alignItems: 'center', width: '100%', padding: '10px 12px',
  border: 'none', cursor: 'pointer', textAlign: 'left',
}
const summaryText = {
  color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 600,
}
const editLink = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  color: T.accent, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
}
const bodyDim = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6, maxWidth: 640,
}
const disclosureBox = {
  marginTop: 16, padding: '12px 14px', borderRadius: T.r.md,
  border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)', maxWidth: 640,
}
const disclosureLine = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6, margin: 0,
}
const pendingText = {
  marginTop: 12, color: T.warning, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6, maxWidth: 640,
}
